/**
 * Every user-visible string on the landing page lives here so the copy can be
 * reviewed against BRAND.md in one file. Nothing in this module may contain a
 * platform name, a latency number, or any wording that frames the product as
 * unlaunched — see the stage rule in BRAND.md.
 */

/** A run of prose where some tokens are API values and must render as `code`. */
export type CodePart = { code: string };
export type Part = string | CodePart;

export const isCode = (part: Part): part is CodePart => typeof part !== 'string';

export type Cta = { label: string; to: string };
export type Fact = { title: string; body: Part[] };
export type Grade = 'high' | 'medium' | 'advertised';
export type ToolStatus = 'live' | 'next' | 'planned';
export type Tool = {
  name: string;
  status: ToolStatus;
  what: string;
  forWhom: string;
  link?: Cta;
};

export const CONTACT_MAILTO =
  'mailto:support@kodifyconsulting.com?subject=AcceptLens%20plans';

export const HERO = {
  eyebrow: 'merchant intelligence toolkit',
  /** Rendered as one sentence; `emphasis` is set in italic Fraunces. */
  headline: {
    before: 'Facts about any merchant, straight from its ',
    emphasis: 'storefront',
    after: '.',
  },
  standfirst:
    'AcceptLens is an API toolkit your analysts and AI agents use to gather specific facts about any merchant — starting with what a store accepts. Every payment method carries its confidence grade.',
  primary: { label: 'Get started free', to: '/signup' } as Cta,
  secondary: { label: 'Read the docs', to: '/docs' } as Cta,
  spec: ['REST', 'MCP', 'OAuth 2.1', '20+ payment methods', '18 platforms'],
};

export const PLATE = {
  eyebrow: 'payment methods api',
  title: 'See what any store accepts.',
  body: 'One request returns the payment methods a store offers at checkout — cards, wallets, instalment providers — together with whether the store is live.',
  facts: [
    {
      title: 'Payment methods',
      body: [
        'Read from the storefront and, where the platform allows, from its own checkout configuration. Instalment providers and wallets included.',
      ],
    },
    {
      title: 'Store status',
      body: [
        'Whether the store is live — ',
        { code: 'active' },
        ', ',
        { code: 'inactive_or_maintenance' },
        ', ',
        { code: 'deleted_store' },
        ' — or the honest ',
        { code: 'platform_not_supported' },
        '.',
      ],
    },
    {
      title: 'Accepted, not advertised',
      body: [
        'Every accepted method carries a grade: ',
        { code: 'high' },
        ' from platform configuration or the live checkout, ',
        { code: 'medium' },
        ' from storefront evidence. Providers that are merely advertised are listed apart, in ',
        { code: 'bnpl_advertised' },
        ', never as accepted.',
      ],
    },
  ] as Fact[],
  link: { label: 'Read the response reference →', to: '/docs' } as Cta,
  /** Labels only — the values are read from the sample response itself. */
  caption: { methods: 'accepted methods', probed: 'probed' },
};

export const WHY = {
  title: 'Why read the storefront?',
  columns: [
    {
      title: 'Self-reported data goes stale.',
      body: 'The form a merchant filled in at onboarding describes that day. The storefront describes today.',
    },
    {
      title: 'Checkout hides acceptance.',
      body: 'Payment methods appear at the last step, behind carts and logins, differently on every platform.',
    },
    {
      title: 'No registry holds it.',
      body: 'Company registers know who the merchant is. None of them know what its store accepts.',
    },
  ],
  closing: 'So we read the storefront.',
};

export const TOOLKIT = {
  eyebrow: 'the toolkit',
  title: 'One toolkit. More tools as the questions come.',
  tools: [
    {
      name: 'Payment Methods API',
      status: 'live' as ToolStatus,
      what: 'What a store accepts and whether it is live — from one URL.',
      forWhom:
        'For onboarding and underwriting teams deciding what a merchant already has.',
      link: { label: 'Docs →', to: '/docs' } as Cta,
    },
    {
      name: 'Store Monitoring',
      status: 'next' as ToolStatus,
      what: "Re-scans on a schedule and tells you when a store's status or payment methods change.",
      forWhom:
        'For portfolio and risk teams that need to know when a merchant moves.',
    },
    {
      name: 'Catalogue Intelligence',
      status: 'planned' as ToolStatus,
      what: 'What the store sells: product counts, categories, price ranges, currency.',
      forWhom: 'For underwriting teams sizing a merchant before terms are set.',
    },
  ] as Tool[],
};

export const SURFACES = {
  title: 'Built for analysts and for the agents they work with.',
  stack: {
    title: 'From your stack',
    code: `curl -X POST https://api.acceptlens.com/v1/identify \\
  -H "Authorization: Bearer $ACCEPTLENS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://store.example"}'`,
    caption: 'Returns the result immediately when one is available, or a job you poll at /v1/jobs/{id}.',
  },
  agent: {
    title: 'From your AI agent',
    code: `claude mcp add --transport http acceptlens \\
  https://api.acceptlens.com/mcp`,
    caption:
      'Works with Claude, ChatGPT, Cursor, VS Code, Codex, Gemini CLI and any client that speaks remote MCP with OAuth.',
    link: { label: 'All install guides →', to: '/docs' } as Cta,
  },
};

export const EVIDENCE = {
  title: 'Evidence, graded.',
  intro: "Every accepted method comes with a grade, in the API's own words.",
  grades: [
    {
      grade: 'high' as Grade,
      body: "read from the platform's own configuration or the live checkout",
    },
    { grade: 'medium' as Grade, body: 'plugin or gateway evidence on the storefront' },
    {
      grade: 'advertised' as Grade,
      body: 'a widget or a badge alone — listed in bnpl_advertised, never as an accepted method',
    },
  ],
  closing: [
    'When we cannot read a store, we say so: ',
    { code: 'platform_not_supported' },
    ', ',
    { code: 'unreachable_*' },
    ', ',
    { code: 'not_a_merchant' },
    '. Those are results, not errors.',
  ] as Part[],
};

export const PRICING = {
  id: 'pricing',
  eyebrow: 'pricing',
  title: 'Start free. Scale on a plan.',
  body: 'Free tier included. No card required. Paid plans are quoted per team — tell us your volume.',
  primary: { label: 'Create a free account', to: '/signup' } as Cta,
  secondary: { label: 'Contact us', to: CONTACT_MAILTO } as Cta,
};

export const FOOTER = {
  wordmarkSrc: '/brand/acceptlens-wordmark.svg',
  wordmarkAlt: 'acceptlens',
  tagline: 'The merchant intelligence toolkit for analysts and AI agents.',
  links: [
    { label: 'Docs', to: '/docs' },
    { label: 'Pricing', to: '#pricing' },
    { label: 'Contact', to: CONTACT_MAILTO },
  ] as Cta[],
  copyright: '© 2026 AcceptLens',
};
